import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import * as E from '@react-email/components';
import { type ActionFunctionArgs, json } from '@remix-run/node';
import { Form, useLoaderData } from '@remix-run/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActionData } from 'react-router';
import Turnstile from 'react-turnstile';
import { ClientOnly } from 'remix-utils/client-only';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { ErrorList, Field, TextareaField } from '#app/components/forms.tsx';
import Particles from '#app/components/particles.tsx';
import { Button } from '#app/components/ui/button.tsx';
import Footer from '#app/components/ui/footer.tsx';
import Header from '#app/components/ui/header.tsx';
import { sendEmail } from '#app/utils/email.server.ts';
import { checkHoneypot } from '#app/utils/honeypot.server.ts';
import { useIsPending } from '#app/utils/misc.tsx';
import { redirectWithToast } from '#app/utils/toast.server.ts';

export const EmailSchema = z
	.string({ required_error: 'Email is required' })
	.email({ message: 'Email is invalid' })
	.min(3, { message: 'Email is too short' })
	.max(100, { message: 'Email is too long' })
	// users can type the email in any case, but we store it in lowercase
	.transform(value => value.toLowerCase());

export const Schema = z.object({
	name: z.string().min(3).max(40),
	number: z.string().min(6).max(12),
	email: EmailSchema,
	message: z.string().min(1).max(500),
});

export async function loader() {
	return {
		siteKey: process.env.TURNSTILE_SITE_KEY,
	};
}

export function ContactEmail({
	email,
	message,
	name,
	number,
}: {
	name: string;
	number: string;
	email: string;
	message: string;
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Gracias por contactarnos, {name}</E.Text>
				</h1>
				<p>
					<E.Text>
						Hemos recibido tu mensaje y te responderemos lo antes posible.
					</E.Text>
				</p>
				<p>
					<E.Text>
						<br />
						<b>Nombre:</b> {name}
						<br />
						<b>Número de teléfono: {number}</b>
						<br />
						<b>Correo electrónico:</b> {email}
						<br />
						<b>Mensaje:</b> {message}
					</E.Text>
				</p>

				<p>
					<E.Text>Saludos,</E.Text>
					<E.Text>El equipo de Explorabit</E.Text>
				</p>
			</E.Container>
		</E.Html>
	);
}

export function NewEmail({
	email,
	message,
	name,
	number,
}: {
	name: string;
	number: string;
	email: string;
	message: string;
}) {
	return (
		<E.Html lang="en" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>
						Nuevo mensaje de contacto de {name} ({email})
					</E.Text>
				</h1>
				<p>
					<E.Text>
						<br />
						<b>Nombre:</b> {name}
						<br />
						<b>Número de teléfono: {number}</b>
						<br />
						<b>Correo electrónico:</b> {email}
						<br />
						<b>Mensaje:</b> {message}
					</E.Text>
				</p>
			</E.Container>
		</E.Html>
	);
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData();
	checkHoneypot(formData);

	const submission = await parseWithZod(formData, {
		schema: Schema,
		async: true,
	});

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		);
	}

	const { message, email, number, name } = submission.value;
	if (email && message) {
		const response = await sendEmail({
			to: email,
			subject: `Explorabit - Muchas gracias por contactarnos`,
			react: (
				<ContactEmail
					email={email}
					message={message}
					name={name}
					number={number}
				/>
			),
		});

		const newEmail = await sendEmail({
			to: 'contacto@explorabit.com',
			subject: `Nuevo mensaje de contacto de ${name} (${email})`,
			react: (
				<NewEmail email={email} message={message} name={name} number={number} />
			),
		});

		if (response.status === 'success' && newEmail.status === 'success') {
			return redirectWithToast('/', {
				title: 'Gracias por contactarnos',
				description:
					'Hemos recibido tu mensaje y te responderemos lo antes posible.',
			});
		} else {
			return json(
				{
					result: submission.reply({ formErrors: [response.error.message] }),
				},
				{
					status: 500,
				},
			);
		}
	}

	return null;
}

export default function Contact() {
	const { t, i18n } = useTranslation();
	const [disabled, setDisabled] = useState(true);
	const { siteKey } = useLoaderData<typeof loader>();
	const actionData = useActionData<typeof action>();
	const isPending = useIsPending();

	const [form, fields] = useForm({
		constraint: getZodConstraint(Schema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: Schema });
		},
		shouldRevalidate: 'onBlur',
	});

	return (
		<>
			<Header />
			<ClientOnly>
				{() => (
					<main className="grow">
						{/* Radial gradient */}
						<div
							className="pointer-events-none absolute left-1/2 top-0 -z-10 flex aspect-square w-[800px] -translate-x-1/2 -translate-y-1/2 items-center justify-center"
							aria-hidden="true"
						>
							<div className="translate-z-0 absolute inset-0 rounded-full bg-teal-500 opacity-30 blur-[120px]"></div>
							<div className="translate-z-0 absolute h-64 w-64 rounded-full bg-teal-400 opacity-70 blur-[80px]"></div>
						</div>

						{/* Particles animation */}
						<Particles className="absolute inset-0 -z-10 h-96" quantity={10} />

						<section className="relative">
							<div className="mx-auto max-w-6xl px-4 sm:px-6">
								<div className="pb-10 pt-32 md:pt-40">
									{/* Hero content */}
									<div className="text-center">
										<div className="inline-flex bg-gradient-to-r from-teal-500 to-teal-200 bg-clip-text pb-3 font-medium text-transparent">
											{t('contact.title')}
										</div>
										<h1 className="h1 bg-gradient-to-r from-slate-200/60 via-slate-200 to-slate-200/60 bg-clip-text pb-6 text-transparent">
											{t('contact.subtitle')}
										</h1>
									</div>
								</div>
							</div>

							<div className="mx-auto max-w-lg px-4 sm:px-6">
								<Form method="POST" {...getFormProps(form)}>
									<HoneypotInputs />
									<Field
										labelProps={{
											htmlFor: fields.name.id,
											children: t('contact.form.name.label'),
										}}
										inputProps={{
											...getInputProps(fields.name, { type: 'text' }),
											autoFocus: true,
											placeholder: t('contact.form.name.placeholder'),
											className: 'form-input w-full',
										}}
										errors={fields.name.errors}
									/>
									<Field
										labelProps={{
											htmlFor: fields.number.id,
											children: t('contact.form.phone.label'),
										}}
										inputProps={{
											...getInputProps(fields.number, { type: 'text' }),
											placeholder: t('contact.form.phone.placeholder'),
											className: 'form-input w-full',
										}}
										errors={fields.number.errors}
									/>

									<Field
										labelProps={{
											htmlFor: fields.email.id,
											children: t('contact.form.email.label'),
										}}
										inputProps={{
											...getInputProps(fields.email, { type: 'email' }),
											autoComplete: 'email',
											placeholder: t('contact.form.email.placeholder'),
											className: 'form-input w-full',
										}}
										errors={fields.email.errors}
									/>

									<TextareaField
										labelProps={{ children: t('contact.form.message.label') }}
										textareaProps={{
											...getTextareaProps(fields.message),
											placeholder: t('contact.form.message.placeholder'),
											className: 'form-input w-full',
										}}
										errors={fields.message.errors}
									/>

									<Turnstile
										sitekey={siteKey}
										className="my-4 w-full"
										language={i18n.language}
										onVerify={() => setDisabled(false)}
									/>

									<ErrorList errors={form.errors} id={form.errorId} />

									<div className="mt-4 flex justify-end">
										<Button
											className="btn group bg-teal-500 text-sm text-white shadow-sm hover:bg-teal-600"
											type="submit"
											disabled={isPending || disabled}
										>
											{t('contact.form.submit')}
										</Button>
									</div>
								</Form>

								<div className="hidden">
									<label
										className="mb-1 block text-sm font-medium text-slate-300"
										htmlFor="contact-reason"
									>
										{t('contact.form.contactReason.label')}
									</label>
									<select
										id="contact-reason"
										name="contact-reason"
										required
										className="form-input w-full"
									>
										<option value="">
											{t('contact.form.contactReason.options.select')}
										</option>
										<option value="information">
											{t('contact.form.contactReason.options.information')}
										</option>
										<option value="demoRequest">
											{t('contact.form.contactReason.options.demoRequest')}
										</option>
										<option value="hireUs">
											{t('contact.form.contactReason.options.hireUs')}
										</option>
										<option value="feedback">
											{t('contact.form.contactReason.options.feedback')}
										</option>
										<option value="work">
											{t('contact.form.contactReason.options.work')}
										</option>
										<option value="other">
											{t('contact.form.contactReason.options.other')}
										</option>
									</select>
								</div>
							</div>

							<div className="mx-auto mt-20 flex max-w-lg flex-col gap-4 px-4 sm:px-6">
								<div>
									{t('contact.contactInfo.email')}{' '}
									<a
										href="mailto:contacto@explorabit.com"
										className="text-teal-500"
									>
										{t('contact.contactInfo.emailAddress')}
									</a>{' '}
									{t('contact.contactInfo.phone')}{' '}
									<a href="tel:+56992588482" className="text-teal-500">
										{'+56 9 9258 8482'}
									</a>
								</div>
								{t('contact.hours')}
							</div>
						</section>
					</main>
				)}
			</ClientOnly>

			<Footer />
		</>
	);
}
